import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

/**
 * Custom hook for fetching report data from API
 */
export function useReportData(endpoint, params = {}, { enabled = true } = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    // A hidden sub-tab skips its fetch; it loads when shown.
    if (!enabled) return;
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`/api/reports/${endpoint}/`, { params });
      setData(response.data);
    } catch (err) {
      console.error(`Error fetching ${endpoint} report:`, err);
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, [endpoint, JSON.stringify(params), enabled]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
}

/**
 * Hook for summary report
 */
export function useSummaryReport(dateParams) {
  return useReportData('summary', dateParams);
}

/**
 * Hook for revenue report
 */
export function useRevenueReport(dateParams) {
  return useReportData('revenue', dateParams);
}

/**
 * Hook for work orders report
 */
export function useWorkOrdersReport(dateParams) {
  return useReportData('work-orders', dateParams);
}

/**
 * Hook for customers report
 */
export function useCustomersReport(dateParams) {
  return useReportData('customers', dateParams);
}

/**
 * Hook for vehicles report
 */
export function useVehiclesReport(dateParams) {
  return useReportData('vehicles', dateParams);
}

/**
 * Hook for employees report (backward compat)
 */
export function useEmployeesReport(dateParams) {
  return useReportData('employees', dateParams);
}

/**
 * Hook for employees performance report
 */
export function usePerformanceReport(dateParams) {
  return useReportData('employees/performance', dateParams);
}

/**
 * Hook for employees worktime report
 */
export function useWorkTimeReport(dateParams) {
  return useReportData('employees/worktime', dateParams);
}

/**
 * Hook for accounting report
 */
export function useAccountingReport(year, month, options) {
  return useReportData('accounting', { year, month }, options);
}

/**
 * Hook for the avoimet laskut ja suoritukset report (month-end open invoices, month's payments)
 */
export function useReceivablesReport(year, month) {
  return useReportData('receivables', { year, month });
}

/**
 * Hook for accounting invoice detail (per-invoice rows)
 */
export function useAccountingInvoiceDetail(year, month) {
  return useReportData('accounting/invoice-detail', { year, month });
}

/**
 * Hook for cash register report
 */
export function useCashRegisterReport(dateParams) {
  return useReportData('cash-register', dateParams);
}

/**
 * Hook for work order profitability report
 */
export function useWorkOrderProfitabilityReport(dateParams) {
  return useReportData('profitability/work-orders', dateParams);
}

/**
 * Hook for vehicle profitability report
 */
export function useVehicleProfitabilityReport(dateParams) {
  return useReportData('profitability/vehicles', dateParams);
}

/**
 * Hook for the inventory value report (stock as it stood at a month end)
 */
export function useInventoryReport(year, month) {
  return useReportData('inventory', { year, month });
}

/**
 * Hook for inventory movements within a month
 */
export function useInventoryMovementsReport(year, month) {
  return useReportData('inventory/movements', { year, month });
}

/**
 * Download invoice summary CSV (same as Settings -> Invoicing "Lataa CSV laskukooste")
 */
export async function downloadInvoiceSummaryCsv(year, month) {
  const response = await axios.get('/api/invoices/summary-csv-download/', {
    params: { year, month },
    responseType: 'blob',
  });

  const blob = new Blob([response.data]);
  const downloadUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = downloadUrl;

  const contentDisposition = response.headers['content-disposition'];
  let filename = `laskukooste_${year}_${String(month).padStart(2, '0')}.csv`;
  if (contentDisposition) {
    const match = contentDisposition.match(/filename="(.+)"/);
    if (match) filename = match[1];
  }

  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(downloadUrl);
}

/**
 * Export report as PDF or Excel
 */
export async function exportReport(reportType, format, params = {}) {
  const url = `/api/reports/${reportType}/export/`;
  const response = await axios.get(url, {
    params: { export_format: format, ...params },
    responseType: 'blob',
  });

  // Create download link
  const blob = new Blob([response.data]);
  const downloadUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = downloadUrl;

  // Get filename from Content-Disposition header or create default
  const contentDisposition = response.headers['content-disposition'];
  let filename = `report.${format === 'xlsx' ? 'xlsx' : 'pdf'}`;
  if (contentDisposition) {
    const match = contentDisposition.match(/filename="(.+)"/);
    if (match) filename = match[1];
  }

  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(downloadUrl);
}
